import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import { dashboardService } from '@/services/dashboardService'
import { Transaction, Notification } from '@/types'
import {
  TrendingUp,
  Users,
  DollarSign,
  CreditCard,
  Activity,
  BarChart3,
  RefreshCw,
  ArrowUp,
  CheckCircle,
  Clock,
  XCircle,
  ShoppingCart,
  Globe,
} from 'lucide-react'

export default function Dashboard() {
  const { user } = useAuthStore()
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)

  const {
    data: stats,
    isLoading: statsLoading,
    refetch: _refetchStats,
  } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: dashboardService.getDashboardStats,
    refetchOnWindowFocus: false,
  })

  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(parseFloat(amount))
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  const resolveNumber = (value?: number) => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value
    }
    return 0
  }

  const formatRate = (value?: number) => resolveNumber(value).toFixed(1)

  const getTrendClass = (value?: number) => {
    const numeric = resolveNumber(value)
    if (numeric > 0) return 'text-green-500'
    if (numeric < 0) return 'text-red-500'
    return 'text-gray-400'
  }

  const getArrowClasses = (value?: number) => {
    const base = `h-4 w-4 mr-1 ${getTrendClass(value)}`
    return resolveNumber(value) < 0 ? `${base} rotate-180` : base
  }

  const formatTrend = (value?: number) => {
    const numeric = resolveNumber(value)
    const sign = numeric > 0 ? '+' : numeric < 0 ? '-' : '+'
    return `${sign}${Math.abs(numeric).toFixed(1)}%`
  }

  return (
    <div
      className="min-h-screen"
      style={pageStyles.container}
    >
      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-4 space-x-4">
                <div>
                  <h1
                    className="text-4xl font-bold"
                    style={pageStyles.text}
                  >
                    Welcome back, {user?.first_name}!
                  </h1>
                  <p
                    className="text-lg"
                    style={pageStyles.textSecondary}
                  >
                    Here's what's happening with your business today
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center"></div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 sm:mb-8">
          <div
            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
            style={cardStyles}
          >
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p
                    className="text-sm font-semibold tracking-wide uppercase"
                    style={pageStyles.textSecondary}
                  >
                    Total Revenue
                  </p>
                  <p
                    className="mt-2 text-3xl font-bold"
                    style={pageStyles.text}
                  >
                    {stats ? formatCurrency(stats.total_revenue) : '$0.00'}
                  </p>
                  <div className="flex items-center mt-1">
                    <ArrowUp className={getArrowClasses(stats?.revenue_change_percentage)} />
                    <span className={`text-xs ${getTrendClass(stats?.revenue_change_percentage)}`}>
                      {stats ? formatTrend(stats.revenue_change_percentage) : '+0.0%'}
                    </span>
                  </div>
                </div>
                <div
                  className="p-4 shadow-lg rounded-2xl"
                  style={pageStyles.primaryBg}
                >
                  <DollarSign className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div
            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
            style={cardStyles}
          >
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p
                    className="text-sm font-semibold tracking-wide uppercase"
                    style={pageStyles.textSecondary}
                  >
                    Total Transactions
                  </p>
                  <p
                    className="mt-2 text-3xl font-bold"
                    style={pageStyles.text}
                  >
                    {stats ? formatNumber(stats.total_transactions) : '0'}
                  </p>
                  <div className="flex items-center mt-1">
                    <ArrowUp className={getArrowClasses(stats?.transactions_change_percentage)} />
                    <span
                      className={`text-xs ${getTrendClass(stats?.transactions_change_percentage)}`}
                    >
                      {stats ? formatTrend(stats.transactions_change_percentage) : '+0.0%'}
                    </span>
                  </div>
                </div>
                <div
                  className="p-4 shadow-lg rounded-2xl"
                  style={{ backgroundColor: branding.secondaryColor }}
                >
                  <CreditCard className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div
            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
            style={cardStyles}
          >
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p
                    className="text-sm font-semibold tracking-wide uppercase"
                    style={pageStyles.textSecondary}
                  >
                    Active Customers
                  </p>
                  <p
                    className="mt-2 text-3xl font-bold"
                    style={pageStyles.text}
                  >
                    {stats ? formatNumber(stats.total_customers) : '0'}
                  </p>
                  <div className="flex items-center mt-1">
                    <ArrowUp className={getArrowClasses(stats?.customers_change_percentage)} />
                    <span
                      className={`text-xs ${getTrendClass(stats?.customers_change_percentage)}`}
                    >
                      {stats ? formatTrend(stats.customers_change_percentage) : '+0.0%'}
                    </span>
                  </div>
                </div>
                <div
                  className="p-4 shadow-lg rounded-2xl"
                  style={{ backgroundColor: branding.secondaryColor }}
                >
                  <Users className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div
            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
            style={cardStyles}
          >
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p
                    className="text-sm font-semibold tracking-wide uppercase"
                    style={pageStyles.textSecondary}
                  >
                    Success Ratings
                  </p>
                  <p
                    className="mt-2 text-3xl font-bold"
                    style={pageStyles.text}
                  >
                    {formatRate(stats?.success_rate)}%
                  </p>
                  <div className="flex items-center mt-1">
                    <ArrowUp className={getArrowClasses(stats?.success_rate_change_percentage)} />
                    <span
                      className={`text-xs ${getTrendClass(stats?.success_rate_change_percentage)}`}
                    >
                      {stats ? formatTrend(stats.success_rate_change_percentage) : '+0.0%'}
                    </span>
                  </div>
                </div>
                <div
                  className="p-4 shadow-lg rounded-2xl"
                  style={pageStyles.primaryBg}
                >
                  <TrendingUp className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Status Overview */}
        <div className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-2">
          <div
            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
            style={cardStyles}
          >
            <div className="card-header">
              <h3
                className="text-xl card-title"
                style={pageStyles.text}
              >
                Transaction Status
              </h3>
              <p
                className="card-description"
                style={pageStyles.textSecondary}
              >
                Distribution of transaction statuses
              </p>
            </div>
            <div className="card-content">
              {statsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw
                    className="w-6 h-6 mr-3 animate-spin"
                    style={{ color: branding.textSecondaryColor }}
                  />
                  <p style={pageStyles.textSecondary}>Loading transaction data...</p>
                </div>
              ) : stats ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="p-2 border rounded-lg"
                        style={{
                          backgroundColor: statusColors.success.backgroundColor,
                          borderColor: statusColors.success.borderColor
                        }}
                      >
                        <CheckCircle
                          className="w-5 h-5"
                          style={{ color: statusColors.success.color }}
                        />
                      </div>
                      <span
                        className="font-medium"
                        style={pageStyles.text}
                      >
                        Completed
                      </span>
                    </div>
                    <div className="text-right">
                      <p
                        className="text-lg font-semibold"
                        style={pageStyles.text}
                      >
                        {formatNumber(stats.completed_transactions)}
                      </p>
                      <p
                        className="text-sm"
                        style={pageStyles.textSecondary}
                      >
                        {formatRate(stats?.completed_rate)}%
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="p-2 border rounded-lg"
                        style={{
                          backgroundColor: statusColors.warning.backgroundColor,
                          borderColor: statusColors.warning.borderColor
                        }}
                      >
                        <Clock
                          className="w-5 h-5"
                          style={{ color: statusColors.warning.color }}
                        />
                      </div>
                      <span
                        className="font-medium"
                        style={pageStyles.text}
                      >
                        Pending
                      </span>
                    </div>
                    <div className="text-right">
                      <p
                        className="text-lg font-semibold"
                        style={pageStyles.text}
                      >
                        {formatNumber(stats.pending_transactions)}
                      </p>
                      <p
                        className="text-sm"
                        style={pageStyles.textSecondary}
                      >
                        {formatRate(stats?.pending_rate)}%
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="p-2 border rounded-lg"
                        style={{
                          backgroundColor: statusColors.error.backgroundColor,
                          borderColor: statusColors.error.borderColor
                        }}
                      >
                        <XCircle
                          className="w-5 h-5"
                          style={{ color: statusColors.error.color }}
                        />
                      </div>
                      <span
                        className="font-medium"
                        style={pageStyles.text}
                      >
                        Failed
                      </span>
                    </div>
                    <div className="text-right">
                      <p
                        className="text-lg font-semibold"
                        style={pageStyles.text}
                      >
                        {formatNumber(stats.failed_transactions)}
                      </p>
                      <p
                        className="text-sm"
                        style={pageStyles.textSecondary}
                      >
                        {formatRate(stats?.failed_rate)}%
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center">
                  <BarChart3
                    className="w-12 h-12 mx-auto mb-4"
                    style={{ color: branding.textSecondaryColor }}
                  />
                  <p style={pageStyles.textSecondary}>No transaction data available</p>
                </div>
              )}
            </div>
          </div>
          <div
            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
            style={cardStyles}
          >
            <div className="card-header">
              <h3
                className="text-xl card-title"
                style={pageStyles.text}
              >
                Sales Channels
              </h3>
              <p
                className="card-description"
                style={pageStyles.textSecondary}
              >
                Active sales channels performance
              </p>
            </div>
            <div className="card-content">
              {statsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw
                    className="w-6 h-6 mr-3 animate-spin"
                    style={{ color: branding.textSecondaryColor }}
                  />
                  <p style={pageStyles.textSecondary}>Loading sales data...</p>
                </div>
              ) : stats ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="p-2 border rounded-lg"
                        style={{
                          backgroundColor: `${branding.secondaryColor}20`,
                          borderColor: `${branding.secondaryColor}30`
                        }}
                      >
                        <ShoppingCart
                          className="w-5 h-5"
                          style={{ color: branding.secondaryColor }}
                        />
                      </div>
                      <span
                        className="font-medium"
                        style={pageStyles.text}
                      >
                        Active Channels
                      </span>
                    </div>
                    <div className="text-right">
                      <p
                        className="text-lg font-semibold"
                        style={pageStyles.text}
                      >
                        {formatNumber(stats.active_sale_shops)}
                      </p>
                      <p
                        className="text-sm"
                        style={pageStyles.textSecondary}
                      >
                        Channels
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="p-2 border rounded-lg"
                        style={{
                          backgroundColor: `${branding.primaryColor}20`,
                          borderColor: `${branding.primaryColor}30`
                        }}
                      >
                        <Globe
                          className="w-5 h-5"
                          style={{ color: branding.primaryColor }}
                        />
                      </div>
                      <span
                        className="font-medium"
                        style={pageStyles.text}
                      >
                        Web Integrations
                      </span>
                    </div>
                    <div className="text-right">
                      <p
                        className="text-lg font-semibold"
                        style={pageStyles.text}
                      >
                        {Math.floor(stats.active_sale_shops * 0.7)}
                      </p>
                      <p
                        className="text-sm"
                        style={pageStyles.textSecondary}
                      >
                        Websites
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="p-2 border rounded-lg"
                        style={{
                          backgroundColor: statusColors.success.backgroundColor,
                          borderColor: statusColors.success.borderColor
                        }}
                      >
                        <Activity
                          className="w-5 h-5"
                          style={{ color: statusColors.success.color }}
                        />
                      </div>
                      <span
                        className="font-medium"
                        style={pageStyles.text}
                      >
                        API Integrations
                      </span>
                    </div>
                    <div className="text-right">
                      <p
                        className="text-lg font-semibold"
                        style={pageStyles.text}
                      >
                        {Math.floor(stats.active_sale_shops * 0.3)}
                      </p>
                      <p
                        className="text-sm"
                        style={pageStyles.textSecondary}
                      >
                        APIs
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center">
                  <ShoppingCart
                    className="w-12 h-12 mx-auto mb-4"
                    style={{ color: branding.textSecondaryColor }}
                  />
                  <p style={pageStyles.textSecondary}>No sales channel data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        {stats && (
          <div className="grid grid-cols-1 gap-6 mt-8 lg:grid-cols-2">
            <div
              className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
              style={cardStyles}
            >
              <div className="card-header">
                <h3
                  className="text-xl card-title"
                  style={pageStyles.text}
                >
                  Recent Transactions
                </h3>
                <p
                  className="card-description"
                  style={pageStyles.textSecondary}
                >
                  Latest transaction activity
                </p>
              </div>
              <div className="card-content">
                <div className="space-y-4">
                  {stats.recent_transactions
                    .slice(0, 5)
                    .map((transaction: Transaction, index: number) => (
                      <div
                        key={transaction.id}
                        className="flex items-center justify-between py-3 border-b last:border-b-0"
                        style={{ borderColor: `${branding.textSecondaryColor}30` }}
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className="flex items-center justify-center w-8 h-8 text-xs font-semibold text-white rounded-full"
                            style={pageStyles.primaryGradient}
                          >
                            {index + 1}
                          </div>
                          <div>
                            <p
                              className="text-sm font-medium"
                              style={pageStyles.text}
                            >
                              {transaction.transaction_reference}
                            </p>
                            <p
                              className="text-xs"
                              style={pageStyles.textSecondary}
                            >
                              {new Date(transaction.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p
                            className="text-sm font-semibold"
                            style={pageStyles.text}
                          >
                            {formatCurrency(transaction.total_amount)}
                          </p>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${transaction.status === 'completed'
                              ? 'text-green-400 bg-green-500/20 border-green-400/30'
                              : transaction.status === 'pending'
                                ? 'text-yellow-400 bg-yellow-500/20 border-yellow-400/30'
                                : 'text-red-400 bg-red-500/20 border-red-400/30'
                              }`}
                          >
                            {transaction.status}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <div
              className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
              style={cardStyles}
            >
              <div className="card-header">
                <h3
                  className="text-xl card-title"
                  style={pageStyles.text}
                >
                  Recent Notifications
                </h3>
                <p
                  className="card-description"
                  style={pageStyles.textSecondary}
                >
                  Latest system notifications
                </p>
              </div>
              <div className="card-content">
                <div className="space-y-4">
                  {stats.recent_notifications
                    .slice(0, 5)
                    .map((notification: Notification, index: number) => (
                      <div
                        key={notification.id}
                        className="flex items-start py-3 space-x-3 border-b last:border-b-0"
                        style={{ borderColor: `${branding.textSecondaryColor}30` }}
                      >
                        <div
                          className="flex items-center justify-center w-8 h-8 text-xs font-semibold text-white rounded-full"
                          style={pageStyles.secondaryGradient}
                        >
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-sm font-medium"
                            style={pageStyles.text}
                          >
                            {notification.title}
                          </p>
                          <p
                            className="mt-1 text-xs line-clamp-2"
                            style={pageStyles.textSecondary}
                          >
                            {notification.message}
                          </p>
                          <p
                            className="mt-1 text-xs"
                            style={{ color: `${branding.textSecondaryColor}80` }}
                          >
                            {new Date(notification.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
